import { generatePkce } from './pkce';
import { StepUpPopup } from './StepUpPopup';
import type { StepUpRequiredDetails } from './types';

export interface StepUpAuthorizationResult {
  // The OAuth authorization code from /authorize (web_message).
  code: string;
  // The PKCE verifier matching the code — presented at the token exchange by whoever
  // performs it (this client, or a page a handoff flow forwards to).
  codeVerifier: string;
  // The popup that ran the flow. With keepOpenOnResolve the caller claims the
  // still-open window via popup.takeWindow() to navigate it onward.
  popup: StepUpPopup;
}

// The AUTHORIZE PHASE of a step-up, shared by every flow that needs an authorization
// code: open the popup blank (SYNCHRONOUSLY — the call must be reached inside the
// user gesture, and this function runs synchronously up to its first `await`),
// generate the PKCE pair, navigate to /authorize, and await the code from the
// web_message. What happens to the code differs per flow — the default token
// provider exchanges it for an access token; a handoff flow forwards it (and the
// kept-open window).
//
// On any failure the popup is torn down here; on success the window follows the
// popup's keepOpenOnResolve config.
export const runStepUpAuthorization = async (
  details: StepUpRequiredDetails,
  { popup = new StepUpPopup() }: { popup?: StepUpPopup } = {},
): Promise<StepUpAuthorizationResult> => {
  const authorization = popup.open();
  // Observe early: if open() rejected outright (popup blocked / not configured), the
  // rejection must be handled during the async PKCE step below and never float as an
  // unhandled rejection before we await `authorization`.
  void authorization.catch(() => undefined);
  try {
    const { codeVerifier, codeChallenge } = await generatePkce();
    popup.authorize(details, codeChallenge);
    const { code } = await authorization;
    return { code, codeVerifier, popup };
  } catch (error) {
    // Tear down the (possibly still-blank) popup on any failure after opening.
    popup.close();
    throw error;
  }
};
